import { actions as espressoCommandActions } from '../batch-framework-commands';
import * as fileActions from './../file/actions';
import {
  espressoContainerSelector,
  WebdriverContainerSelector,
  manualRdcContainerSelector,
} from './selectors';
import * as jobActions from './../job/actions';
import { actions as webdriverCommandActions } from './../../webdriver-commands';

export const NAMESPACE = 'job-container';

export const LS_TEST_DETAILS_COLUMNS_RATIO = 'testDetailsColumnsRatio';
export const INITIAL_COLUMNS_RATIO = 0.33;

export const PREVIEW_MODE_GALLERY = 'gallery';
export const PREVIEW_MODE_VIDEO = 'video';

export const METADATA_SIZE_PARTITION_PX = 400;
export const API_FETCH_INTERVAL = 5000;
export const NOTIFICATION_THROTTLE_INTERVAL = 3000;
export const SEARCH_DEBOUNCE_INTERVAL = 600;
export const FORMAT_FULL_DATETIME = 'yyyy-MM-dd HH:mm:ss';
export const ASSETS_NOT_AVAILABLE_YET = "Job hasn't finished running";

export const MISSING_ASSETS_MESSAGE = 'Assets for this test are no longer available on ' +
  'Sauce Labs servers. Please visit ' +
  '<a href="https://docs.saucelabs.com/dev/error-messages/index.html" ' +
  'target="_blank">docs.saucelabs.com</a> ' +
  'to learn more.';

export const DELETE_JOB_MODAL_ID = 'deleteJobModal';

export const COMMAND_LIST_CONTAINER_ID = 'commandListContainer';

export const MEDIA_TAB_VIDEO = 'media';
export const TEST_DETAILS_TAB_TEST_CASES = 'test cases';
export const TEST_DETAILS_TAB_COMMANDS = 'commands';
export const TEST_DETAILS_TAB_LOGS = 'logs';
export const TEST_DETAILS_TAB_METADATA = 'metadata';
export const TEST_DETAILS_TAB_INTERACTIONS = 'interactions';
export const TEST_DETAILS_TAB_SCREENSHOTS = 'screenshots';
export const TEST_DETAILS_TAB_CRASHES = 'crashes';
export const TEST_DETAILS_TAB_NETWORK = 'network';
export const TEST_DETAILS_TAB_VITALS = 'vitals';

export const TAB_KEY_TO_TITLE = Object.freeze({
  [MEDIA_TAB_VIDEO]: 'Video',
  [TEST_DETAILS_TAB_TEST_CASES]: 'Test Cases',
  [TEST_DETAILS_TAB_COMMANDS]: 'Commands',
  [TEST_DETAILS_TAB_LOGS]: 'Logs',
  [TEST_DETAILS_TAB_METADATA]: 'Metadata',
  [TEST_DETAILS_TAB_INTERACTIONS]: 'Interactions',
  [TEST_DETAILS_TAB_SCREENSHOTS]: 'Screenshots',
  [TEST_DETAILS_TAB_CRASHES]: 'Crashes',
  [TEST_DETAILS_TAB_NETWORK]: 'Network',
  [TEST_DETAILS_TAB_VITALS]: 'Vitals',
});

export const SELENIUM_JOB_METADATA_ORDER_LIST = [
  { label: 'Application Summary', name: 'application_summary', type: 'json' },
  { label: 'App', name: 'app', type: 'link' },
  { label: 'ID', name: 'id' },
  { label: 'Passed', name: 'passed', type: 'boolean' },
  { label: 'Name', name: 'name' },
  { label: 'Build', name: 'build' },
  { label: 'Tags', name: 'tags' },
  { label: 'Browser', name: 'browser' },
  { label: 'Browser version', name: 'browser_version' },
  { label: 'Server OS', name: 'os' },
  { label: 'Automation backend', name: 'automation_backend' },
  { label: 'Client', name: 'client' },
  { label: 'Webdriver Protocol', name: 'webdriver_protocol' },
  { label: 'Owner', name: 'owner' },
  { label: 'Creation time', name: 'creation_time', type: 'time' },
  { label: 'Start time', name: 'start_time', type: 'time' },
  { label: 'End time', name: 'end_time', type: 'time' },
  { label: 'Modification time', name: 'modification_time', type: 'time' },
  { label: 'Wait time', name: 'wait_time' },
  { label: 'Error', name: 'error' },
  { label: 'Selenium version', name: 'selenium_version' },
  { label: 'Proxied', name: 'proxied', type: 'boolean' },
  { label: 'Assigned tunnel id', name: 'assigned_tunnel_id' },
  { label: 'Base config', name: 'base_config', type: 'json' },
  { label: 'Record screenshots', name: 'record_screenshots', type: 'boolean' },
  { label: 'Record video', name: 'record_video', type: 'boolean' },
  { label: 'Custom Data', name: 'custom-data', type: 'json' },
  { label: 'Public', name: 'public' },
  { label: 'Manual', name: 'manual', type: 'boolean' },
  { label: 'Breakpointed', name: 'breakpointed' },
  { label: 'Screenshots', name: 'screenshots', type: 'screenshot' },
  { label: 'Full Device Descriptor', name: 'full_device_descriptor', type: 'json' },
];

export const OPTIONAL_FIELDS = [
  'webdriver_protocol',
];

export const JS_FRAMEWORKS = [
  'cypress',
  'playwright',
  'puppeteer',
  'testcafe',
  'cucumber',
];

export const JS_FRAMEWORKS_METADATA_KEYS_TO_HIDE = [
  'application_summary',
  'webdriver_protocol',
  'wait_time',
  'selenium_version',
  'manual',
  'breakpointed',
  'full_device_descriptor',
];

export const PERFORMANCE_TRACE_COMMAND_DELAY_IN_MS = 5 * 1000;
export const SAUCE_JANKINESS_CUSTOM_COMMAND = 'sauce:jankinessCheck';

const xmlConfig = {
  headers: {
    Accept: 'application/xml',
  },
};

const textPlainConfig = {
  headers: {
    Accept: 'text/plain',
  },
};

export const FILE_TO_URL_MAPPING_RDC = Object.freeze({
  'appium.log': {
    name: 'appium.log',
    urlKey: 'framework_log_url',
  },
  'commands.json': {
    name: 'commands.json',
    urlKey: 'requests_url',
  },
  'device.log': {
    name: 'device.log',
    urlKey: 'device_log_url',
  },
  'xcuitest.log': {
    name: 'xcuitest.log',
    urlKey: 'framework_log_url',
  },
  'junit.xml': {
    name: 'junit.xml',
    urlKey: 'junit_log_url',
    config: xmlConfig,
  },
  'network.har': {
    name: 'network.har',
    urlKey: 'network_log_url',
    config: textPlainConfig,
  },
  'crash.log': {
    name: 'crash.json',
    urlKey: 'crash_log_url',
    config: textPlainConfig,
  },
});

// Espresso and XCUITest are testing frameworks for Android and iOS apps respectively.
export const AUTOMATED_JOBS_NATIVE_FRAMEWORKS = ['espresso', 'xcuitest'];

// RDC jobs(Appium and Native Frameworks) assets list

const deviceLogs = {
  urlKey: 'device_log_url',
  assetName: 'device.log',
};

const nativeFrameworkTestCases = {
  urlKey: 'test_cases_url',
  parser: espressoCommandActions.parseNativeFrameworkTestCases,
};

const appiumCommands = {
  urlKey: 'requests_url',
  assetName: 'commands.json',
  parser: webdriverCommandActions.parseAppiumLog,
};

const frameworkLogs = {
  urlKey: 'framework_log_url',
  formatter: fileActions.formatRdcLogFile,
};

const junitLog = {
  urlKey: 'junit_log_url',
  assetName: 'junit.xml',
  config: xmlConfig,
};

const networkLogs = {
  urlKey: 'network_log_url',
  assetName: 'network.har',
  config: textPlainConfig,
};

const testfairyLogs = {
  urlKey: 'testfairy_log_url',
  parser: jobActions.parseTestfairyLogs,
  config: textPlainConfig,
};

const crashLogs = {
  urlKey: 'crash_log_url',
  assetName: 'crash.log',
  config: textPlainConfig,
  formatter: fileActions.formatCrashLogFile,
  parser: jobActions.parseCrashLogs,
};

export const deviceEvents = {
  config: textPlainConfig,
  parser: jobActions.parseDeviceEvents,
};

const commonRdcAssetsList = [deviceLogs, networkLogs, testfairyLogs, crashLogs];

export const jobAssetsList = {
  espresso: {
    logsList: [...commonRdcAssetsList, nativeFrameworkTestCases, junitLog],
    selector: espressoContainerSelector,
  },
  xcuitest: {
    logsList: [...commonRdcAssetsList, { ...frameworkLogs, assetName: 'xcuitest.log' }, nativeFrameworkTestCases, junitLog],
    selector: espressoContainerSelector,
  },
  appium: {
    logsList: [...commonRdcAssetsList, appiumCommands, { ...frameworkLogs, assetName: 'appium.log' }],
    selector: WebdriverContainerSelector,
  },
  manualRdc: {
    logsList: [...commonRdcAssetsList],
    selector: manualRdcContainerSelector,
  },
};
export const PERFORMANCE_DISABLE_COMMAND = 'sauce:performanceDisable';
export const PERFORMANCE_ENABLE_COMMAND = 'sauce:performanceEnable';

export const COMMAND_TAB_TITLE_KIND = {
  TestCases: 'test cases',
  Commands: 'commands',
};

export const FALLBACK_CRASH_TITLE = 'Something went wrong';

export const DEFAULT_SELECTED_JOB_ASSET_NAME = deviceLogs.assetName;
